import React, { useState, useEffect } from 'react';
import { useUser } from '../../context/UserContext';
import chatAPI from '../../services/ChatAPI';
import './ConversationList.css';

interface Conversation {
  chatId: string;
  otherUser: {
    id: string;
    firstName: string;
    lastName: string;
    imageUrl?: string;
  };
  lastMessage: {
    content: string;
    senderId: string;
    createdAt: string;
  };
  unreadCount: number;
  updatedAt: string;
}

interface ConversationListProps {
  onSelectConversation: (conversation: Conversation) => void;
  selectedConversationId?: string;
}

const ConversationList: React.FC<ConversationListProps> = ({ 
  onSelectConversation, 
  selectedConversationId 
}) => {
  const { user } = useUser();
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadConversations();
  }, []);

  const loadConversations = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await chatAPI.getConversations();
      
      // Transform backend data format to frontend format
      const transformedConversations = (response.conversations || []).map((conv: any) => {
        const profile = conv.otherParticipant?.profile || {};
        
        // Extract name from profile - try fullName first, then name, then individual fields
        let fullNameStr = profile.fullName || profile.name || '';
        
        // If no fullName but has individual name fields
        if (!fullNameStr && (profile.firstName || profile.lastName)) {
          fullNameStr = `${profile.firstName || ''} ${profile.lastName || ''}`.trim();
        }
        
        // Fallback to 'Unknown User' if still empty
        if (!fullNameStr) {
          fullNameStr = 'Unknown User';
        }
        
        // Split into first and last name
        const nameParts = fullNameStr.trim().split(' ');
        const firstName = nameParts[0] || '';
        const lastName = nameParts.slice(1).join(' ') || '';
        
        // Log for debugging
        console.log('Profile data:', {
          profile,
          fullNameStr,
          firstName,
          lastName
        });
        
        return {
          chatId: conv.id,
          otherUser: {
            id: conv.otherParticipant?.id || '',
            firstName: firstName,
            lastName: lastName,
            imageUrl: profile.imageUrl || profile.avatar || profile.photoURL
          },
          lastMessage: conv.lastMessage ? {
            content: conv.lastMessage.content || '',
            senderId: conv.lastMessage.senderId || '',
            createdAt: conv.lastMessage.createdAt || new Date().toISOString()
          } : {
            content: '',
            senderId: '',
            createdAt: conv.lastMessageAt || new Date().toISOString()
          },
          unreadCount: conv.unreadCount || 0,
          updatedAt: conv.updatedAt || new Date().toISOString()
        };
      });
      
      setConversations(transformedConversations);
    } catch (error: any) {
      console.error('Failed to load conversations:', error);
      setError('Failed to load conversations');
    } finally {
      setLoading(false);
    }
  };

  const formatTime = (timestamp: string) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diff = now.getTime() - date.getTime();
    const hours = Math.floor(diff / (1000 * 60 * 60));
    
    if (hours < 24) {
      return date.toLocaleTimeString('en-US', { 
        hour: '2-digit', 
        minute: '2-digit',
        hour12: true 
      });
    } else {
      return date.toLocaleDateString('en-US', { 
        month: 'short', 
        day: 'numeric'
      });
    }
  };

  const getOtherUserName = (conversation: Conversation) => {
    return `${conversation.otherUser.firstName} ${conversation.otherUser.lastName}`;
  };

  const getLastMessagePreview = (conversation: Conversation) => {
    const isOwnMessage = conversation.lastMessage.senderId === user?.id;
    const prefix = isOwnMessage ? 'You: ' : '';
    return prefix + conversation.lastMessage.content;
  };

  if (loading) {
    return (
      <div className="conversation-list">
        <div className="conversation-list-header">
          <h5>Conversations</h5>
        </div>
        <div className="conversation-list-content">
          <div className="text-center py-4">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
            <p className="mt-2 text-muted">Loading conversations...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="conversation-list">
        <div className="conversation-list-header">
          <h5>Conversations</h5>
        </div>
        <div className="conversation-list-content">
          <div className="error-state">
            <div className="error-state-icon">
              <i className="fas fa-exclamation-triangle"></i>
            </div>
            <h6 className="error-state-title">Unable to load conversations</h6>
            <p className="error-state-description">
              We're having trouble connecting to our servers. This might be a temporary issue.
            </p>
            <div className="error-state-actions">
              <button 
                className="btn btn-primary btn-sm me-2"
                onClick={loadConversations}
              >
                <i className="fas fa-redo me-1"></i>
                Try Again
              </button>
              <button 
                className="btn btn-outline-secondary btn-sm"
                onClick={() => window.location.reload()}
              >
                <i className="fas fa-refresh me-1"></i>
                Refresh Page
              </button>
            </div>
            <div className="error-state-help">
              <small className="text-muted">
                If the problem persists, please check your internet connection or try again later.
              </small>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (conversations.length === 0) {
    return (
      <div className="conversation-list">
        <div className="conversation-list-header">
          <h5>Conversations</h5>
        </div>
        <div className="conversation-list-content">
          <div className="empty-state">
            <div className="empty-state-icon">
              <i className="fas fa-comments"></i>
            </div>
            <h6 className="empty-state-title">No conversations yet</h6>
            <p className="empty-state-description">
              Start chatting with your family matches to begin meaningful conversations!
            </p>
            <div className="empty-state-features">
              <div className="feature-item">
                <i className="fas fa-heart text-primary"></i>
                <span>Connect with family members</span>
              </div>
              <div className="feature-item">
                <i className="fas fa-shield-alt text-success"></i>
                <span>Secure messaging</span>
              </div>
              <div className="feature-item">
                <i className="fas fa-clock text-info"></i>
                <span>Real-time updates</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="conversation-list">
      <div className="conversation-list-header">
        <h5>Conversations</h5>
        <button 
          className="btn btn-outline-primary btn-sm"
          onClick={loadConversations}
          title="Refresh"
        >
          <i className="fas fa-sync-alt"></i>
        </button>
      </div>
      <div className="conversation-list-content">
        {conversations.map((conversation) => (
          <div
            key={conversation.chatId}
            className={`conversation-item ${
              selectedConversationId === conversation.chatId ? 'active' : ''
            }`}
            onClick={() => onSelectConversation(conversation)}
          >
            <div className="conversation-avatar">
              <img
                src={conversation.otherUser.imageUrl || "https://encrypted-tbn0.gstatic.com/images?q=tbn:ANd9GcRPoAJ0jJpb24AFIkHZ4RfZTXFAHLLjxm33Fg&s"}
                alt={getOtherUserName(conversation)}
                className="avatar-img"
              />
              {conversation.unreadCount > 0 && (
                <span className="unread-badge">
                  {conversation.unreadCount > 99 ? '99+' : conversation.unreadCount}
                </span>
              )}
            </div>
            <div className="conversation-details">
              <div className="conversation-header">
                <h6 className="conversation-name">{getOtherUserName(conversation)}</h6>
                <span className="conversation-time">
                  {formatTime(conversation.updatedAt)}
                </span>
              </div>
              <div className="conversation-preview">
                <p className={`conversation-message ${conversation.unreadCount > 0 ? 'unread' : ''}`}>
                  {getLastMessagePreview(conversation)}
                </p>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default ConversationList;
